import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { t } from "@/i18n";
import { useMe } from "@/lib/auth";
import { Button, Card, Badge, Select, Input, Spinner, EmptyState } from "@/components/ui";
import {
  useAssignments,
  useAssignmentTypes,
  useDuplicateAssignment,
  useUpdateAssignment,
  type Assignment,
} from "@/lib/queries";

export const Route = createFileRoute("/app/assignments")({
  component: RepositoryPage,
});

const LANGS = ["", "en", "es"];
const STATUSES = ["", "draft", "ready", "archived"];

function RepositoryPage() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [lang, setLang] = useState("");
  const [status, setStatus] = useState("");
  const [tag, setTag] = useState("");

  const { data: types } = useAssignmentTypes();
  // Default view hides archived items (spec §8) unless explicitly filtered.
  const { data: rows, isLoading } = useAssignments({
    q: q || undefined,
    type: type || undefined,
    lang: lang || undefined,
    tag: tag || undefined,
    status: status || undefined,
  });
  const duplicate = useDuplicateAssignment();
  const update = useUpdateAssignment();
  const { data: me } = useMe();
  const visible = (rows ?? []).filter((r) => (status ? true : r.status !== "archived"));
  const typeName = (a: Assignment) =>
    types?.find((x) => x.id === a.assignmentTypeId)?.translations.find(
      (x) => x.languageCode === a.languageCode,
    )?.name ??
    types?.find((x) => x.id === a.assignmentTypeId)?.translations.find((x) => x.languageCode === "en")
      ?.name ??
    a.typeCode ??
    "";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t("repo.title")}</h1>
        <Link to="/app/assignments/new" search={{ type: undefined }}>
          <Button>＋ {t("repo.new")}</Button>
        </Link>
      </div>

      <Card className="flex flex-wrap items-center gap-2 p-3">
        <Input
          placeholder={t("repo.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-48 flex-1"
        />
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">{t("repo.allTypes")}</option>
          {(types ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {t(`types.${x.code}`, { defaultValue: x.code })}
            </option>
          ))}
        </Select>
        <Select value={lang} onChange={(e) => setLang(e.target.value)}>
          {LANGS.map((l) => (
            <option key={l} value={l}>
              {l ? l.toUpperCase() : t("repo.allLanguages")}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s ? t(`assignments.status.${s}`) : t("repo.allStatuses")}
            </option>
          ))}
        </Select>
        <Input
          placeholder={t("repo.tagFilter")}
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="w-32"
        />
      </Card>

      {isLoading ? (
        <Spinner label={t("app.loading")} />
      ) : visible.length === 0 ? (
        <EmptyState>{t("repo.empty")}</EmptyState>
      ) : (
        <ul className="space-y-3">
          {visible.map((a) => (
            <li key={a.id}>
              <Card className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{a.title}</span>
                    <Badge tone="blue">{typeName(a)}</Badge>
                    <Badge>{a.languageCode.toUpperCase()}</Badge>
                    <Badge
                      tone={
                        a.status === "ready" ? "green" : a.status === "archived" ? "red" : "amber"
                      }
                    >
                    {t(`assignments.status.${a.status}`)}
                    </Badge>
                    {a.ownerEmail && me && a.ownerEmail !== me.email && (
                      <Badge tone="purple">{t("repo.sharedFrom", { email: a.ownerEmail })}</Badge>
                    )}
                  </div>
                  {a.tags && <p className="mt-1 text-xs text-muted-foreground">🏷 {a.tags}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link to="/app/assignments/$id" params={{ id: a.id }}>
                    <Button variant="outline">{t("common.edit")}</Button>
                  </Link>
                  <Link to="/app/assign/$id" params={{ id: a.id }}>
                    <Button>{t("common.assign")}</Button>
                  </Link>
                  <Button
                    variant="ghost"
                    disabled={duplicate.isPending}
                    onClick={() => duplicate.mutate(a.id)}
                  >
                    ⧉ {t("common.duplicate")}
                  </Button>
                  {a.canEdit !== false && (
                    <Button
                      variant="ghost"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: a.id,
                          patch: { status: a.status === "archived" ? "draft" : "archived" },
                        })
                      }
                    >
                      {a.status === "archived" ? `↩ ${t("common.restore")}` : `🗄 ${t("common.archive")}`}
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}